import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { useStore } from "../store/StoreContext";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import WordReveal from "../components/WordReveal";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Date, new to old" },
];

const COLS = { 2: "grid-cols-2", 3: "grid-cols-2 lg:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4" };
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");

function Group({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-line py-4">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-[13px] font-medium py-1" data-testid={`filter-group-${slugify(title)}`}>
        {title}
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && <div className="mt-4 space-y-2.5">{children}</div>}
    </div>
  );
}

const Check = ({ label, checked, onChange, testId, count }) => (
  <label className="flex items-center justify-between gap-3 text-[13px] text-charcoal/80 cursor-pointer group">
    <span className="flex items-center gap-3">
      <input type="checkbox" data-testid={testId} checked={checked} onChange={onChange} className="accent-[#1B1B1B] w-3.5 h-3.5 rounded" />
      <span className="group-hover:text-ink transition-colors duration-200">{label}</span>
    </span>
    {count !== undefined && <span className="text-[11px] text-mocha">({count})</span>}
  </label>
);

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const { catalog } = useStore();
  const [products, setProducts] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [cols, setCols] = useState(3);

  const state = {
    tag: params.get("tag") || "",
    category: params.get("category") || "",
    material: params.get("material") || "",
    finish: params.get("finish") || "",
    color: params.get("color") || "",
    search: params.get("search") || "",
    sort: SORTS.some((option) => option.value === params.get("sort")) ? params.get("sort") : "featured",
  };

  useEffect(() => {
    api.filters().then(setFilterOptions).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    Object.entries(state).forEach(([k, v]) => {
      if (v !== "" && v !== false) q[k] = v;
    });
    api.products(q).then((data) => {
      setProducts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === false) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const categoryCounts = useMemo(
    () => catalog.reduce((m, p) => ({ ...m, [p.category]: (m[p.category] || 0) + 1 }), {}),
    [catalog]
  );

  const title = state.search
    ? `“${state.search}”`
    : state.tag === "new"
    ? "New Arrivals"
    : state.tag === "bestseller"
    ? "Bestsellers"
    : state.category || "Export Catalogue";

  const activeFilters = ["tag", "category", "material", "finish", "color", "search"].filter((k) => state[k]);

  const Filters = () => (
    <div>
      <Group title="Collections">
        {[
          ["All pieces", ""],
          ["New Arrivals", "new"],
          ["Bestsellers", "bestseller"],
        ].map(([label, val]) => (
          <button
            key={label}
            data-testid={`filter-tag-${val || "all"}`}
            onClick={() => update("tag", val)}
            className={`block text-[13px] transition-colors duration-200 ${state.tag === val ? "text-ink font-medium" : "text-charcoal/70 hover:text-ink"}`}
          >
            {label}
          </button>
        ))}
      </Group>
      <Group title="Categories">
        {(filterOptions?.categories || []).map((c) => (
          <Check
            key={c}
            label={c}
            count={categoryCounts[c]}
            testId={`filter-category-${slugify(c)}`}
            checked={state.category === c}
            onChange={(e) => update("category", e.target.checked ? c : "")}
          />
        ))}
      </Group>
      <Group title="Material">
        {(filterOptions?.materials || []).map((m) => (
          <Check key={m} label={m} testId={`filter-material-${slugify(m)}`} checked={state.material === m} onChange={(e) => update("material", e.target.checked ? m : "")} />
        ))}
      </Group>
      <Group title="Finish" defaultOpen={false}>
        {(filterOptions?.finishes || []).map((f) => (
          <Check key={f} label={f} testId={`filter-finish-${slugify(f)}`} checked={state.finish === f} onChange={(e) => update("finish", e.target.checked ? f : "")} />
        ))}
      </Group>
      <Group title="Colour" defaultOpen={false}>
        {(filterOptions?.colors || []).map((c) => (
          <Check key={c} label={c} testId={`filter-color-${slugify(c)}`} checked={state.color === c} onChange={(e) => update("color", e.target.checked ? c : "")} />
        ))}
      </Group>
    </div>
  );

  return (
    <div className="pb-28" data-testid="shop-page">
      <SEO title={`${title} — S H & SONS ®`} description="Explore home décor for international trade: lighting, tabletop, accessories and gifting in metal, glass, wood and marble. Made in India." />

      <div className="pt-8 sm:pt-12 pb-10 text-center px-4">
        <WordReveal key={title} as="h1" text={title} className="h-display text-[clamp(2rem,9vw,3.75rem)] sm:text-7xl lg:text-[112px]" data-testid="shop-title" />
      </div>

      <div className="mx-auto max-w-[1500px] px-4 sm:px-8 flex items-center justify-between gap-3">
        <button data-testid="filters-open-button" onClick={() => setDrawer(true)} className="pill-dark px-5 py-2.5">
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} /> Filter {activeFilters.length > 0 && `(${activeFilters.length})`}
        </button>
        <p className="hidden sm:block text-[12px] text-mocha" data-testid="product-count">{products.length} pieces</p>
        <div className="flex items-center gap-2 sm:gap-3">
          <select
            value={state.sort}
            onChange={(e) => update("sort", e.target.value)}
            data-testid="sort-select"
            aria-label="Sort catalogue"
            className="rounded-full border border-line bg-white px-4 py-2.5 text-[12px] outline-none cursor-pointer hover:border-ink transition-colors duration-200"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <div className="hidden lg:flex items-center rounded-full border border-line p-1" data-testid="grid-columns-toggle">
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                data-testid={`grid-cols-${n}`}
                onClick={() => setCols(n)}
                className={`w-8 h-8 rounded-full text-[11px] transition-colors duration-200 ${cols === n ? "bg-ink text-white" : "text-mocha hover:text-ink"}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="mx-auto max-w-[1500px] px-4 sm:px-8 mt-5 flex flex-wrap items-center gap-2" data-testid="active-filters">
          {activeFilters.map((k) => (
            <button key={k} data-testid={`active-filter-${k}`} onClick={() => update(k, "")} className="pill-outline px-3 py-1.5 text-[11px] gap-1.5">
              {k === "tag" ? (state.tag === "new" ? "New Arrivals" : "Bestsellers") : state[k]}
              <X className="w-3 h-3" strokeWidth={1.5} />
            </button>
          ))}
          <button data-testid="filters-clear-all" onClick={() => setParams({})} className="text-[11px] uppercase tracking-[0.2em] text-mocha hover:text-ink ml-2">Clear all</button>
        </div>
      )}

      <div className="mx-auto max-w-[1500px] px-4 sm:px-8 mt-12">
        {loading ? (
          <div className={`grid ${COLS[cols]} gap-x-6 gap-y-14`}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse text-center">
                <div className="bg-cream aspect-square rounded-[22px]" />
                <div className="mt-6 h-3 bg-cream w-1/2 mx-auto rounded" />
                <div className="mt-2 h-3 bg-cream w-1/4 mx-auto rounded" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center" data-testid="shop-empty-state">
            <p className="text-3xl font-light text-charcoal">Nothing here yet</p>
            <p className="mt-2 text-[13px] text-mocha">Try clearing a filter or two.</p>
            <button data-testid="shop-empty-clear" onClick={() => setParams({})} className="pill-dark mt-8">Show all pieces</button>
          </div>
        ) : (
          <div className={`grid ${COLS[cols]} gap-x-6 gap-y-14`} data-testid="product-grid">
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {drawer && (
          <>
            <motion.div
              data-testid="filters-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] z-[80]"
              onClick={() => setDrawer(false)}
            />
            <motion.aside
              data-testid="filters-panel"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 left-0 w-[88%] max-w-[380px] bg-white z-[90] flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-[76px] border-b border-line">
                <h2 className="text-[22px] font-light">Filter</h2>
                <button data-testid="filters-close" onClick={() => setDrawer(false)} aria-label="Close filters" className="flex items-center gap-2 p-2 -mr-2 hover:text-brass transition-colors duration-200">
                  <span className="text-[11px] uppercase tracking-[0.3em]">Close</span>
                  <X className="w-5 h-5" strokeWidth={1.25} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-2">
                <Filters />
              </div>
              <div className="px-6 py-5 border-t border-line flex gap-3">
                <button data-testid="filters-reset" onClick={() => setParams({})} className="pill-outline flex-1">Clear</button>
                <button data-testid="filters-apply" onClick={() => setDrawer(false)} className="pill-dark flex-1">Show {products.length}</button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
